import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Icon } from '@iconify/react'

// antd
import { Avatar, message, Typography, Image, Row, Dropdown } from 'antd'

// slices
import { loginState } from '../login/login.slice'
import { usersState } from '../users/users.slice'
import { projectsState } from '../projects/projects.slice'
import {
    mainTaskById,
    selectTaskUpdate,
    taskById,
    tasksState,
    updateComment,
    updateMainTaskComment,
} from './tasks.slice'
import { isTaskMutationSuccess } from './tasks.service'

// components
import MediaViewer from './components/mediaViewer'
import DeletePopUp from '../../components/deletePopUp/deletePopUp'
import { showActionNotification } from '../../components/notification'
import MentionEditor from '../../components/MentionEditor/MentionEditor'

// helpers
import { formatCommentDate } from '../../helpers/formatCommentDate'

// styles
import styles from './comments.section.module.css'

function CommentsSection({
    taskListKind = 'support',
    mentionData,
    setFilteredMembers,
    setCommentChange,
    commentChange,
    onTaskUpdate,
}) {
    const isMainTask = taskListKind === 'main'
    const { Text } = Typography

    const dispatch = useDispatch()

    const { userProfile } = useSelector(loginState)
    const { selectedProject, currentProjectData } = useSelector(projectsState)

    const { selectedTask, selectedSubTask, formSubTask } =
        useSelector(tasksState)

    const { clientModalUsersListData = [] } = useSelector(usersState)

    const [comment, setComment] = useState()
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteButtonLoading, setDeleteButtonLoading] = useState(false)
    const [hoveredCommentId, setHoveredCommentId] = useState(null)
    const [dropdownOpen, setDropdownOpen] = useState({})
    const [editingCommentId, setEditingCommentId] = useState(null)
    const [editCommentData, setEditCommentData] = useState({
        value: '',
        mentionIds: [],
    })
    const [editCommentLoading, setEditCommentLoading] = useState(false)
    const [filteredMembersForEdit, setFilteredMembersForEdit] = useState([])

    // Get filtered members for edit
    useEffect(() => {
        if (clientModalUsersListData?.length > 0) {
            let filterUsers = []
            clientModalUsersListData?.length > 0 &&
                clientModalUsersListData.forEach((member) => {
                    if (member?._id !== userProfile?._id) {
                        filterUsers = [
                            ...filterUsers,
                            {
                                id: member?._id,
                                display: `${member?.name}`,
                                profilePicture: member?.profilePicture,
                                email: member?.email,
                            },
                        ]
                    }
                })
            setFilteredMembersForEdit(filterUsers)
        } else {
            setFilteredMembersForEdit([])
        }
    }, [clientModalUsersListData, userProfile?._id])

    useEffect(() => {
        if (selectedSubTask && formSubTask) {
            setCommentChange(selectedSubTask?.comments)
        } else if (selectedTask) {
            setCommentChange(selectedTask?.data?.comments)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTask, selectedSubTask, formSubTask])

    // // Get User List
    // useEffect(() => {
    //     dispatch(resetClientModalUsers())
    //     dispatch(
    //         usersListForClientModal({
    //             skip: 0,
    //             filter: {},
    //             limit: 10,
    //             search: '',
    //         })
    //     )
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [])

    // DELETE COMMENT
    async function deleteComment() {
        const taskId = selectedTask?.data?._id

        const removePayload = {
            id: taskId,
            data: {
                fieldName: 'remove',
                commentId: comment?._id,
            },
        }
        const result = await dispatch(
            isMainTask
                ? updateMainTaskComment(removePayload)
                : updateComment(removePayload)
        )
        const data = result?.payload?.data
        if (data) {
            const { success, message: checkMessage } = data
            const isSuccess = isMainTask
                ? isTaskMutationSuccess(data)
                : Boolean(success)
            setComment()
            if (isSuccess) {
                // Show custom notification at bottom left using global component
                showActionNotification({
                    message: 'Comment was removed from',
                    subMessage: selectedTask?.data?.projectId?.name,
                    actionButtonText: 'Undo',
                    notificationKey: 'comment-deleted',
                })

                setDeleteButtonLoading(false)
                setDeleteModal(false)
                await dispatch(
                    isMainTask
                        ? mainTaskById({ _id: taskId })
                        : taskById({ _id: taskId })
                )
                if (onTaskUpdate) {
                    onTaskUpdate()
                }
            } else {
                setDeleteModal(false)
                setDeleteButtonLoading(false)
                if (checkMessage) {
                    message.error(checkMessage)
                } else {
                    message.error('Something went wrong, try again later.')
                }
            }
        }
    }

    async function checkUser() {
        if (clientModalUsersListData?.length > 0) {
            let filterUsers = []

            clientModalUsersListData?.length > 0 &&
                clientModalUsersListData.forEach((member) => {
                    if (
                        member?._id !== userProfile?._id &&
                        !mentionData?.mentionDataList?.includes(member._id)
                    ) {
                        filterUsers = [
                            ...filterUsers,
                            {
                                id: member?._id,
                                display: `${member?.name}`,
                                profilePicture: member?.profilePicture,
                                email: member?.email,
                            },
                        ]
                    }
                })

            setFilteredMembers(filterUsers)
        } else {
            setFilteredMembers([])
        }
    }

    useEffect(() => {
        // TEAM MEMBER DATA SET WITH FILTER
        if (currentProjectData) {
            checkUser()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedProject,
        mentionData?.mentionDataList,
        clientModalUsersListData,
    ])
    // HANDLE CHANGE MENTIONS COMMENT

    const escapeHtml = (str) =>
        String(str ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')

    const escapeAttrUrl = (url) => String(url ?? '').replace(/"/g, '&quot;')

    /** API may send mentionId as [{ _id, name, profilePicture }] or legacy string ids */
    const normalizeMentionEntries = (mentionIds) => {
        if (!Array.isArray(mentionIds)) return []
        return mentionIds
            .map((m) => {
                if (m && typeof m === 'object' && (m._id || m.id)) {
                    const id = String(m._id || m.id)
                    const name = (m.name || m.email || '').trim()
                    if (!name) return null
                    return {
                        _id: id,
                        name,
                        profilePicture: m.profilePicture,
                    }
                }
                if (typeof m === 'string' && clientModalUsersListData?.length) {
                    const u = clientModalUsersListData.find(
                        (user) => user._id === m
                    )
                    if (!u) return null
                    return {
                        _id: u._id,
                        name: (u.name || u.email || '').trim(),
                        profilePicture: u.profilePicture,
                    }
                }
                return null
            })
            .filter(Boolean)
    }

    const toMentionIdPayload = (mentions) =>
        (mentions || []).map((m) =>
            m && typeof m === 'object' && m._id ? m._id : m
        )

    const getMentionInitials = (userName) =>
        String(userName || '')
            .split(' ')
            .map((n) => n[0])
            .filter(Boolean)
            .join('')
            .slice(0, 2)
            .toUpperCase()

    // Get avatar color based on user name/email
    const getAvatarColor = (name) => {
        const colors = [
            'rgb(139, 92, 246)', // purple
            'rgb(59, 130, 246)', // light blue
            'rgb(34, 197, 94)', // green
            'rgb(239, 68, 68)', // red
            'rgb(245, 158, 11)', // orange
        ]
        if (!name) return colors[0]
        const hash = name
            .split('')
            .reduce((acc, char) => acc + char.charCodeAt(0), 0)
        return colors[hash % colors.length]
    }

    const buildMentionPillHtml = (user) => {
        const userName = user?.name || user?.email || ''
        if (!userName) return ''
        const avatarColor = getAvatarColor(userName)
        const initials = escapeHtml(getMentionInitials(userName))
        const safeName = escapeHtml(userName)
        const pic = user?.profilePicture
        const innerAvatar = pic
            ? `<img src="${escapeAttrUrl(pic)}" alt="" width="18" height="18" style="width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;" />`
            : initials

        return `<span class="${styles.mentionPill}"><span class="${styles.mentionPillAvatar}" style="background-color:${avatarColor};">${innerAvatar}</span><span class="${styles.mentionPillName}">${safeName}</span></span>`
    }

    // Render @mentions as pill chips (avatar + name, no @); supports Quill embeds + plain text
    const renderContentWithMentions = (content, mentionIds) => {
        if (!content) return ''
        const entries = normalizeMentionEntries(mentionIds)
        if (entries.length === 0) return content

        if (typeof document !== 'undefined') {
            try {
                const div = document.createElement('div')
                div.innerHTML = content
                const mentionNodes = div.querySelectorAll(
                    'span[data-id][data-value]'
                )
                mentionNodes.forEach((node) => {
                    if (!node.querySelector?.('.ql-mention-value')) return
                    const id = node.getAttribute('data-id')
                    const val = node.getAttribute('data-value')
                    const entry =
                        entries.find((e) => e._id === id) ||
                        entries.find((e) => e.name === val)
                    if (!entry) return
                    const wrap = document.createElement('span')
                    wrap.innerHTML = buildMentionPillHtml(entry)
                    const pill = wrap.firstElementChild
                    if (pill && node.parentNode) {
                        node.parentNode.replaceChild(pill, node)
                    }
                })
                let html = div.innerHTML
                const sorted = [...entries].sort(
                    (a, b) => (b.name?.length || 0) - (a.name?.length || 0)
                )
                sorted.forEach((user) => {
                    const name = user.name
                    if (!name) return
                    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    const re = new RegExp(
                        `@${escaped}(?=\\s|$|[.,!?;:]|&nbsp;)`,
                        'gi'
                    )
                    html = html.replace(re, () => buildMentionPillHtml(user))
                })
                return html
            } catch {
                // fall through
            }
        }

        let processedContent = content
        const sorted = [...entries].sort(
            (a, b) => (b.name?.length || 0) - (a.name?.length || 0)
        )
        sorted.forEach((user) => {
            const name = user.name
            if (!name) return
            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const re = new RegExp(`@${escaped}(?=\\s|$|[.,!?;:]|&nbsp;)`, 'gi')
            processedContent = processedContent.replace(re, () =>
                buildMentionPillHtml(user)
            )
        })
        return processedContent
    }

    // Handle edit comment
    const handleEditComment = (comment) => {
        setEditingCommentId(comment?._id)
        setEditCommentData({
            value: comment?.content || '',
            mentionIds: comment?.mentionId || [],
        })
    }

    // Handle cancel edit
    const handleCancelEdit = () => {
        setEditingCommentId(null)
        setEditCommentData({
            value: '',
            mentionIds: [],
        })
    }

    // Handle save edited comment
    const handleSaveEdit = async () => {
        if (!editingCommentId) return

        setEditCommentLoading(true)
        try {
            const taskId = formSubTask
                ? selectedSubTask?._id
                : selectedTask?.data?.[0]?._id || selectedTask?._id
            const updatePayload = {
                id: taskId,
                data: {
                    fieldName: 'update',
                    commentId: editingCommentId,
                    comment: {
                        content: editCommentData.value,
                        mentionId: toMentionIdPayload(
                            editCommentData.mentionIds
                        ),
                    },
                },
            }
            const result = await dispatch(
                isMainTask
                    ? updateMainTaskComment(updatePayload)
                    : updateComment(updatePayload)
            )

            const data = result?.payload?.data
            if (data) {
                const { success, message: checkMessage } = data
                const isSuccess = isMainTask
                    ? isTaskMutationSuccess(data)
                    : Boolean(success)
                if (isSuccess) {
                    message.success(
                        checkMessage || 'Comment updated successfully'
                    )
                    setEditingCommentId(null)
                    setEditCommentData({
                        value: '',
                        mentionIds: [],
                    })
                    // Refresh task data
                    const refreshResult = await dispatch(
                        isMainTask
                            ? mainTaskById({ _id: taskId })
                            : taskById({ _id: taskId })
                    )
                    const refreshData = refreshResult?.payload?.data
                    if (refreshData && !isMainTask) {
                        dispatch(selectTaskUpdate(refreshData?.data[0]))
                    }
                } else {
                    message.error(checkMessage || 'Failed to update comment')
                }
            }
        } catch (error) {
            message.error('Failed to update comment')
        } finally {
            setEditCommentLoading(false)
        }
    }

    // Handle edit comment change
    const handleEditCommentChange = (html, plainText, mentions) => {
        setEditCommentData({
            value: html,
            mentionIds: mentions,
        })
    }

    return (
        <>
            <div>
                <DeletePopUp
                    previewDelete={deleteModal}
                    onDelete={() => {
                        setDeleteButtonLoading(true)
                        deleteComment()
                    }}
                    loadingButton={deleteButtonLoading}
                    onCancel={() => {
                        setDeleteButtonLoading(false)
                        setDeleteModal(false)
                    }}
                    modalTitle="Delete Comment"
                    pageKey="Comment"
                />

                {commentChange?.length > 0 ? (
                    <div
                        style={{
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: '8px 0',
                        }}
                    >
                        {commentChange.map((data, index) => {
                            const avatarColor = getAvatarColor(
                                data?.username || data?.user?.email
                            )
                            const userInitials = data?.username
                                ? data.username
                                      .split(' ')
                                      .map((n) => n[0])
                                      .filter(Boolean)
                                      .join('')
                                      .slice(0, 2)
                                      .toUpperCase()
                                : ''

                            return (
                                <div
                                    key={data?._id || index}
                                    style={{
                                        display: 'flex',
                                        gap: '12px',
                                        padding: '8px 0',
                                        position: 'relative',
                                    }}
                                    onMouseEnter={() =>
                                        setHoveredCommentId(data?._id)
                                    }
                                    onMouseLeave={() =>
                                        setHoveredCommentId(null)
                                    }
                                >
                                    {/* Avatar */}
                                    <div style={{ flexShrink: 0 }}>
                                        {data?.user?.profilePicture ? (
                                            <Image
                                                src={data.user.profilePicture}
                                                height={40}
                                                width={40}
                                                style={{
                                                    borderRadius: '50%',
                                                }}
                                                preview={false}
                                            />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor:
                                                        avatarColor,
                                                    cursor: 'pointer',
                                                    height: '28px',
                                                    width: '28px',
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {userInitials}
                                            </Avatar>
                                        )}
                                    </div>

                                    {/* Comment Content */}
                                    <div
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                            position: 'relative',
                                        }}
                                    >
                                        {/* Header: Name and Date */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                position: 'relative',
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: '#1f2937',
                                                }}
                                            >
                                                {data?.username ||
                                                    data?.user?.name ||
                                                    data?.user?.email ||
                                                    'Unknown User'}
                                            </Text>
                                            <Text
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#9ca3af',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {formatCommentDate(
                                                    data?.createdDate
                                                )}
                                            </Text>

                                            {/* Hover Reactions - Right Side */}
                                            {hoveredCommentId === data?._id && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        right: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        backgroundColor: '#fff',
                                                        borderRadius: '6px',
                                                        padding: '4px 8px',
                                                        boxShadow:
                                                            '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                        zIndex: 10,
                                                    }}
                                                >
                                                    <Dropdown
                                                        menu={{
                                                            items: [
                                                                // {
                                                                //     key: 'edit',
                                                                //     label: (
                                                                //         <span
                                                                //             style={{
                                                                //                 color: '#1f2937',
                                                                //             }}
                                                                //         >
                                                                //             Edit
                                                                //             comment
                                                                //         </span>
                                                                //     ),
                                                                //     onClick:
                                                                //         () => {
                                                                //             handleEditComment(
                                                                //                 data
                                                                //             )
                                                                //             setDropdownOpen(
                                                                //                 {
                                                                //                     ...dropdownOpen,
                                                                //                     [data?._id]: false,
                                                                //                 }
                                                                //             )
                                                                //         },
                                                                //     disabled:
                                                                //         data?.userId !==
                                                                //         userProfile?._id,
                                                                // },
                                                                {
                                                                    key: 'delete',
                                                                    label: (
                                                                        <span
                                                                            style={{
                                                                                color: '#ef4444',
                                                                            }}
                                                                        >
                                                                            Delete
                                                                            comment
                                                                        </span>
                                                                    ),
                                                                    onClick:
                                                                        () => {
                                                                            setDeleteModal(
                                                                                true
                                                                            )
                                                                            setComment(
                                                                                data
                                                                            )
                                                                            setDropdownOpen(
                                                                                {
                                                                                    ...dropdownOpen,
                                                                                    [data?._id]: false,
                                                                                }
                                                                            )
                                                                        },
                                                                    disabled:
                                                                        data?.userId !==
                                                                        userProfile?._id,
                                                                },
                                                            ],
                                                        }}
                                                        trigger={['click']}
                                                        open={
                                                            dropdownOpen[
                                                                data?._id
                                                            ] || false
                                                        }
                                                        onOpenChange={(
                                                            open
                                                        ) => {
                                                            setDropdownOpen({
                                                                ...dropdownOpen,
                                                                [data?._id]:
                                                                    open,
                                                            })
                                                        }}
                                                        placement="bottomRight"
                                                        overlayClassName={
                                                            styles.dropdownMenu
                                                        }
                                                    >
                                                        <div
                                                            style={{
                                                                cursor: 'pointer',
                                                                padding:
                                                                    '2px 4px',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                borderRadius:
                                                                    '4px',
                                                                transition:
                                                                    'background-color 0.2s',
                                                            }}
                                                            onMouseEnter={(
                                                                e
                                                            ) => {
                                                                e.currentTarget.style.backgroundColor =
                                                                    '#f3f4f6'
                                                            }}
                                                            onMouseLeave={(
                                                                e
                                                            ) => {
                                                                e.currentTarget.style.backgroundColor =
                                                                    'transparent'
                                                            }}
                                                        >
                                                            <Icon
                                                                icon="mdi:dots-horizontal"
                                                                style={{
                                                                    fontSize:
                                                                        '16px',
                                                                    color: '#6b7280',
                                                                }}
                                                            />
                                                        </div>
                                                    </Dropdown>
                                                </div>
                                            )}
                                        </div>

                                        {/* Comment Text with Mentions or Edit View */}
                                        {editingCommentId === data?._id ? (
                                            <div
                                                style={{
                                                    marginTop: '8px',
                                                }}
                                            >
                                                <MentionEditor
                                                    projectId={
                                                        selectedProject?._id ||
                                                        currentProjectData?._id
                                                    }
                                                    excludeUserIds={toMentionIdPayload(
                                                        editCommentData.mentionIds ||
                                                            []
                                                    )}
                                                    handleChangeMentions={
                                                        handleEditCommentChange
                                                    }
                                                    value={
                                                        editCommentData.value
                                                    }
                                                    clearTrigger={false}
                                                    loadingButton={false}
                                                    onClickComment={() => {}}
                                                    disabledButton={false}
                                                    isEditMode={true}
                                                    onCancelEdit={
                                                        handleCancelEdit
                                                    }
                                                    onSaveEdit={handleSaveEdit}
                                                    saveLoading={
                                                        editCommentLoading
                                                    }
                                                    saveDisabled={
                                                        !editCommentData.value ||
                                                        editCommentData.value.trim()
                                                            .length === 0
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className={
                                                    styles.commentContent
                                                }
                                                style={{
                                                    color: '#374151',
                                                    fontSize: '14px',
                                                    lineHeight: '1.5',
                                                    wordBreak: 'break-word',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: renderContentWithMentions(
                                                        data?.content,
                                                        data?.mentionId
                                                    ),
                                                }}
                                            />
                                        )}

                                        {Array.isArray(data?.attachments) &&
                                            data.attachments.length > 0 && (
                                                <Row
                                                    style={{
                                                        width: '100%',
                                                        flexFlow: 'inherit',
                                                        marginTop: '20px',
                                                        columnGap: '10px',
                                                        rowGap: '10px',
                                                        flexWrap: 'wrap',
                                                    }}
                                                    className={`fileTask ${styles.commentAttachments}`}
                                                >
                                                    {data.attachments.some(
                                                        (item) => item
                                                    ) && (
                                                        <MediaViewer
                                                            mediaFiles={data.attachments.filter(
                                                                (item) => item
                                                            )}
                                                            handleDelete={() => {
                                                                // Attachments in comments are read-only, no delete functionality
                                                            }}
                                                        />
                                                    )}
                                                </Row>
                                            )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '28px 20px',
                            width: '100%',
                        }}
                    >
                        <Icon
                            icon="mdi:comment-outline"
                            width={40}
                            height={40}
                            style={{
                                color: '#94a3b8',
                                marginBottom: 10,
                            }}
                        />
                        <Text
                            style={{
                                fontSize: '14px',
                                color: '#64748b',
                                fontWeight: 500,
                                margin: 0,
                                lineHeight: 1.45,
                            }}
                        >
                            No comments yet
                        </Text>
                    </div>
                )}
            </div>
        </>
    )
}

export default CommentsSection
